/**
 * Money accounts and transfers.
 *
 * Under the ['money'] key, and every write also refreshes Money & Stock: a
 * transfer changes what the drawer should hold.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CashBankDashboard,
  CashBankTransactions,
  CashFlow,
  CashFlowGranularity,
  MoneyAccountInput,
  MoneyAccountUpdate,
  MoneyAccounts,
  MoneyCustody,
  MoneyJournal,
  MoneyMovementView,
  MoneyStatementInput,
  MoneyStatementView,
  MoneyTransferInput,
  MoneyTransferView,
} from '@cenox/shared';
import { api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';

export function useMoneyAccounts(asOf?: string): UseQueryResult<MoneyAccounts> {
  const path = useBusinessPath();
  const { can } = useAuth();
  return useQuery({
    queryKey: ['money', 'accounts', path(''), asOf ?? 'today'],
    queryFn: () => api.get<MoneyAccounts>(path(`/money/accounts${asOf ? `?asOf=${asOf}` : ''}`)),
    // Reading balances is a reports permission; without it the pickers fall
    // back to "the default for this method" and say nothing more.
    enabled: can('reports:read'),
    staleTime: 30_000,
  });
}

export function useAccountMovements(
  id: string | null,
  range: { from: string; to: string },
): UseQueryResult<MoneyMovementView[]> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['money', 'movements', path(''), id, range],
    queryFn: async () =>
      (await api.get<{ data: MoneyMovementView[] }>(
        path(`/money/accounts/${id!}/movements?from=${range.from}&to=${range.to}`),
      )).data,
    enabled: id !== null,
  });
}

export function useTransfers(): UseQueryResult<MoneyTransferView[]> {
  const path = useBusinessPath();
  const { can } = useAuth();
  return useQuery({
    queryKey: ['money', 'transfers', path('')],
    queryFn: async () => (await api.get<{ data: MoneyTransferView[] }>(path('/money/transfers'))).data,
    enabled: can('reports:read') || can('cash:deposit'),
  });
}

/** The cash this person holds, where they may hand it, and what waits on them. */
export function useMyCustody(): UseQueryResult<MoneyCustody> {
  const path = useBusinessPath();
  const { can } = useAuth();
  return useQuery({
    queryKey: ['money', 'custody', path('')],
    queryFn: () => api.get<MoneyCustody>(path('/money/custody')),
    enabled: can('cash:deposit') || can('cash:reconcile'),
    staleTime: 15_000,
  });
}

/** Cash flow by day, week, month or year. */
export function useCashFlow(
  q: { from: string; to: string; granularity: CashFlowGranularity } & Record<string, string | undefined>,
  enabled = true,
): UseQueryResult<CashFlow> {
  const path = useBusinessPath();
  const { can } = useAuth();
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v) search.set(k, v);
  return useQuery({
    queryKey: ['money', 'cash-flow', path(''), q],
    queryFn: () => api.get<CashFlow>(path(`/money/cash-flow?${search.toString()}`)),
    enabled: enabled && can('reports:read'),
    staleTime: 15_000,
  });
}

/** Every movement in a period as balanced entries. */
export function useJournal(
  q: { from: string; to: string } & Record<string, string | undefined>,
  enabled: boolean,
): UseQueryResult<MoneyJournal> {
  const path = useBusinessPath();
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v) search.set(k, v);
  return useQuery({
    queryKey: ['money', 'journal', path(''), q],
    queryFn: () => api.get<MoneyJournal>(path(`/money/journal?${search.toString()}`)),
    enabled,
  });
}

export function useStatements(): UseQueryResult<MoneyStatementView[]> {
  const path = useBusinessPath();
  const { can } = useAuth();
  return useQuery({
    queryKey: ['money', 'statements', path('')],
    queryFn: async () => (await api.get<{ data: MoneyStatementView[] }>(path('/money/statements'))).data,
    enabled: can('reports:read'),
  });
}

export function useRecordStatement(): UseMutationResult<{ data: { id: string } }, Error, MoneyStatementInput> {
  const path = useBusinessPath();
  const invalidate = useInvalidateMoney();
  return useMutation({
    mutationFn: (input) => api.post<{ data: { id: string } }>(path('/money/statements'), input),
    onSuccess: invalidate,
  });
}

export function useVoidStatement(): UseMutationResult<{ ok: true }, Error, { id: string; reason: string }> {
  const path = useBusinessPath();
  const invalidate = useInvalidateMoney();
  return useMutation({
    mutationFn: ({ id, reason }) => api.post<{ ok: true }>(path(`/money/statements/${id}/void`), { reason }),
    onSuccess: invalidate,
  });
}

function useInvalidateMoney(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ['money'] });
    await client.invalidateQueries({ queryKey: ['accountability'] });
  };
}

export function useCreateAccount(): UseMutationResult<{ data: { id: string } }, Error, MoneyAccountInput> {
  const path = useBusinessPath();
  const invalidate = useInvalidateMoney();
  return useMutation({
    mutationFn: (input) => api.post<{ data: { id: string } }>(path('/money/accounts'), input),
    onSuccess: invalidate,
  });
}

export function useUpdateAccount(): UseMutationResult<{ ok: true }, Error, { id: string } & MoneyAccountUpdate> {
  const path = useBusinessPath();
  const invalidate = useInvalidateMoney();
  return useMutation({
    mutationFn: ({ id, ...input }) => api.patch<{ ok: true }>(path(`/money/accounts/${id}`), input),
    onSuccess: invalidate,
  });
}

export function useRecordTransfer(): UseMutationResult<
  { data: { id: string; replayed: boolean } },
  Error,
  MoneyTransferInput
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateMoney();
  return useMutation({
    mutationFn: (input) => api.post<{ data: { id: string; replayed: boolean } }>(path('/money/transfers'), input),
    onSuccess: invalidate,
  });
}

export function useVoidTransfer(): UseMutationResult<{ ok: true }, Error, { id: string; reason: string }> {
  const path = useBusinessPath();
  const invalidate = useInvalidateMoney();
  return useMutation({
    mutationFn: ({ id, reason }) => api.post<{ ok: true }>(path(`/money/transfers/${id}/void`), { reason }),
    onSuccess: invalidate,
  });
}

/** The Cash & Bank dashboard for a period and filters. */
export function useCashBankDashboard(
  q: { from: string; to: string } & Record<string, string | undefined>,
  enabled = true,
): UseQueryResult<CashBankDashboard> {
  const path = useBusinessPath();
  const { can } = useAuth();
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v) search.set(k, v);
  return useQuery({
    queryKey: ['money', 'dashboard', path(''), q],
    queryFn: () => api.get<CashBankDashboard>(path(`/money/dashboard?${search.toString()}`)),
    enabled: enabled && can('reports:read'),
    staleTime: 15_000,
  });
}

/** Query string for the transaction history; empty values are left out. */
export function transactionsQuery(q: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') search.set(k, String(v));
  return search.toString();
}

/** Cash & Bank transaction history: every movement in the period, filtered on the server. */
export function useCashBankTransactions(
  q: { from: string; to: string } & Record<string, string | number | undefined>,
  enabled = true,
): UseQueryResult<CashBankTransactions> {
  const path = useBusinessPath();
  const { can } = useAuth();
  return useQuery({
    queryKey: ['money', 'transactions', path(''), q],
    queryFn: () => api.get<CashBankTransactions>(path(`/money/transactions?${transactionsQuery(q)}`)),
    enabled: enabled && can('reports:read'),
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  });
}

/** Every matching row, for an export. */
export function useFetchAllTransactions(): (q: Record<string, string | number | undefined>) => Promise<CashBankTransactions> {
  const path = useBusinessPath();
  return (q) => api.get<CashBankTransactions>(path(`/money/transactions?${transactionsQuery({ ...q, limit: 2000, offset: 0 })}`));
}

export function useCompleteTransfer(): UseMutationResult<
  { ok: true },
  Error,
  { id: string; completedOn: string; reference?: string }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateMoney();
  return useMutation({
    mutationFn: ({ id, completedOn, reference }) =>
      api.post<{ ok: true }>(path(`/money/transfers/${id}/complete`), { completedOn, ...(reference ? { reference } : {}) }),
    onSuccess: invalidate,
  });
}

export interface MemberOption {
  userId: string;
  fullName: string | null;
  email: string | null;
}

/** Members, for naming who holds an account's cash. Owners and admins can list them. */
export function useMemberOptions(enabled: boolean): UseQueryResult<MemberOption[]> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['money', 'members', path('')],
    queryFn: async () => {
      const r = await api.get<{ data: Array<{ userId: string; fullName?: string | null; name?: string | null; email?: string | null }> }>(path('/members'));
      return r.data.map((m) => ({ userId: m.userId, fullName: m.fullName ?? m.name ?? null, email: m.email ?? null }));
    },
    enabled,
    staleTime: 60_000,
  });
}

/** Upload a deposit slip or transfer receipt; returns the attachment id. */
export function useUploadEvidence(): UseMutationResult<{ id: string }, Error, File> {
  const path = useBusinessPath();
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return api.upload<{ id: string }>(path('/attachments'), body);
    },
  });
}
